import { 
    gql, 
    useMutation, 
    ApolloError 
} from "@apollo/client";
import { UpdateMerchantAdminMutationMutation, UpdateMerchantAdminMutationMutationVariables } from "../../../../../schemaTypes";

export const updateMerchantAdminMutation = gql`
  mutation UpdateMerchantAdminMutation(
        $id: String!
        $input: UpdateBusinessInfoInput!
    ) {
    updateMerchantAdmin(
        id: $id
        input: $input
    ) {
      errors {
        path
        message
      }
      message
      logoUrl
      bannerUrl
    }
  }
`;

interface Props {
  onMessage?: (message: string) => void;
  children: (
    data: {
      submit: (
        values: UpdateMerchantAdminMutationMutationVariables & {
          logoFile?: File;
          bannerFile?: File;
        }
      ) => Promise<{
        errors: ({ path: string; message: string } | null)[] | null;
        message?: string;
        logoUrl?: string;
        bannerUrl?: string;
      }>;
      loading: boolean;
      error?: ApolloError;
    }
  ) => React.ReactElement | null;
}

function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

export const UpdateMerchantAdminController: React.FC<Props> = ({ children, onMessage }) => {
  const [updateMerchantAdmin, { loading, error }] = useMutation<
    UpdateMerchantAdminMutationMutation,
    UpdateMerchantAdminMutationMutationVariables
  >(updateMerchantAdminMutation);

  const submit = async (
    values: UpdateMerchantAdminMutationMutationVariables & { logoFile?: File; bannerFile?: File }
  ) => {
    const { logoFile, bannerFile, ...rest } = values;
    let logoUrl = rest.input.logoUrl;
    let bannerUrl = rest.input.bannerUrl;
    if (logoFile) {
      logoUrl = await fileToBase64(logoFile);
    }
    if (bannerFile) {
      bannerUrl = await fileToBase64(bannerFile);
    }
    const { data } = await updateMerchantAdmin({
      variables: {
        id: rest.id,
        input: {
          ...rest.input,
          logoUrl,
          bannerUrl,
        },
      },
    });

    const errors = data?.updateMerchantAdmin?.errors ?? null;
    const message = data?.updateMerchantAdmin?.message ?? undefined;
    const logoUrlResp = data?.updateMerchantAdmin?.logoUrl ?? undefined;
    const bannerUrlResp = data?.updateMerchantAdmin?.bannerUrl ?? undefined;

    if (message && onMessage) {
      onMessage(message);
    }

    return {
      errors,
      logoUrl: logoUrlResp,
      bannerUrl: bannerUrlResp,
      message,
    };
  };

  return children({ submit, loading, error });
};